var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { CustomElement, P, Watch, PropertyConverters, Utils, CustomElementUtils } from '@pacem/pacem-core';
import { Colors } from '@pacem/pacem-foundation';
import { Geometry } from '@pacem/pacem-numerical';
import { StalePropertyFlag, isMesh, isUi3DObject, Pacem3DAdapterElement, Pacem3DObjectElement, Pacem3DGroupElement, Pacem3DLightElement, Pacem3DCameraElement, Pacem3DPerspectiveCameraElement, Pacem3DOrthographicCameraElement, Pacem3DMeshElement } from '../types';
import { MeshGeometry, LineGeometry } from '../geometry';
import { AdapterUtils } from './shared';
import { isStandardMaterial, isPhongMaterial, isLambertMaterial, isLineMaterial, isMaterial } from '../materials/material';
//namespace Pacem.Components.Drawing3D {
function isMeshGeometry(obj) {
    return AdapterUtils.isMeshGeometry(obj);
}
function isVector3D(obj) {
    return AdapterUtils.isVector3D(obj);
}
function flattenVectorArray(array) {
    return AdapterUtils.flattenVectorArray(array);
}
const threeJsVersion = '0.152.2', lastOrbitCtrlJsVersion = '0.147.0';
;
const consts = {
    VERSION: threeJsVersion,
    API_URI: `https://unpkg.com/three@${threeJsVersion}/build/three.min.js`,
    ORBIT_URI: `https://unpkg.com/three@${lastOrbitCtrlJsVersion}/examples/js/controls/OrbitControls.js`,
    LOADERS: [
    //`https://unpkg.com/three@${threeJsVersion}/examples/js/loaders/OBJLoader.js`,
    //`https://unpkg.com/three@${threeJsVersion}/examples/js/loaders/MTLLoader.js`,
    ],
    DEFAULT_MATERIAL: function () {
        return new THREE.MeshStandardMaterial({ color: '#069', flatShading: true });
    }
};
function matrix3DToMatrix4(m) {
    const matrix = new THREE.Matrix4();
    matrix.set(m.m11, m.m12, m.m13, m.m14, m.m21, m.m22, m.m23, m.m24, m.m31, m.m32, m.m33, m.m34, m.offsetX, m.offsetY, m.offsetZ, m.m44);
    return matrix;
}
// as of https://codepen.io/discoverthreejs/full/vwVeZB
function fitCameraToSelection(camera, controls /* OrbitControls */, selection, fitOffset = 1.333) {
    const box = new THREE.Box3();
    for (const object of selection) {
        box.expandByObject(object);
    }
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxSize = Math.max(size.x, size.y, size.z);
    const fitHeightDistance = maxSize / (2 * Math.atan(Math.PI * camera.fov / 360));
    const fitWidthDistance = fitHeightDistance / camera.aspect;
    const distance = fitOffset * Math.max(fitHeightDistance, fitWidthDistance);
    const hasControls = !Utils.isNull(controls);
    var direction;
    if (hasControls) {
        direction = controls.target.clone()
            .sub(camera.position)
            .normalize()
            .multiplyScalar(distance);
        controls.maxDistance = distance * 10;
        controls.target.copy(center);
    }
    camera.near = distance / 100;
    camera.far = distance * 100;
    camera.updateProjectionMatrix();
    if (hasControls) {
        camera.position.copy(controls.target).sub(direction);
        controls.update();
    }
}
function flattenMatrix3D(m) {
    return [
        // right way to translate the matrix3D to matrix4
        m.m11, m.m21, m.m31, m.offsetX,
        m.m12, m.m22, m.m32, m.offsetY,
        m.m13, m.m23, m.m33, m.offsetZ,
        m.m14, m.m24, m.m34, m.m44,
    ];
}
function nodeGeometryToBufferGeometry(mesh) {
    const geometry = new THREE.BufferGeometry();
    const vertices = flattenVectorArray(mesh.positions);
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    if (mesh instanceof MeshGeometry) {
        if (!Utils.isNullOrEmpty(mesh.triangleIndices)) {
            geometry.setIndex(mesh.triangleIndices);
        }
        const vertices = flattenVectorArray(mesh.positions);
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        const uvs = flattenVectorArray(mesh.textureCoordinates);
        geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
        if (Utils.isNullOrEmpty(mesh.normals)) {
            geometry.computeVertexNormals();
        }
        else {
            const normals = flattenVectorArray(mesh.normals);
            geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
        }
    }
    return geometry;
}
function lightToLight(item) {
    var light;
    switch (item.type) {
        case 'spot':
            const spotLight = new THREE.SpotLight();
            spotLight.target.position.set(item.target.x, item.target.y, item.target.z);
            light = spotLight;
            break;
        default:
            light = new THREE.PointLight();
            break;
    }
    return light;
}
function materialToMaterial(material) {
    if (Utils.isNull(material)) {
        return null;
    }
    const lineBase = {
        color: typeof material.color === 'string' ? material.color : Colors.stringify(material.color),
        opacity: material.opacity ?? 1,
        transparent: true,
        wireframe: material.wireframe ?? false,
        visible: material.visible ?? true
    };
    const base = Utils.extend({}, lineBase, { wireframe: material.wireframe ?? false });
    const mipMapMaterial = (m) => {
        if (material.map) {
            m['map'] = new THREE.TextureLoader().load(material.map, _ => {
                // console.log('texture loaded!');
                m.needsUpdate = true;
            });
        }
        return m;
    };
    if (isStandardMaterial(material)) {
        const std = mipMapMaterial(new THREE.MeshStandardMaterial(Utils.extend(base, {
            roughness: material.roughness,
            metalness: material.metalness,
            emissive: material.emissiveColor,
            flatShading: material.flatShading,
            refractionRatio: material.refractionRatio,
        })));
        return std;
    }
    else if (isPhongMaterial(material)) {
        const phong = mipMapMaterial(new THREE.MeshPhongMaterial(Utils.extend(base, {
            shininess: material.shininess,
            emissive: material.emissiveColor,
            flatShading: material.flatShading,
            specular: material.specularColor,
            reflectivity: material.reflectivity,
            refractionRatio: material.refractionRatio,
        })));
        return phong;
    }
    else if (isLambertMaterial(material)) {
        const lambert = mipMapMaterial(new THREE.MeshLambertMaterial(Utils.extend(base, {
            emissive: material.emissiveColor,
            reflectivity: material.reflectivity,
            refractionRatio: material.refractionRatio,
        })));
        return lambert;
    }
    else if (isLineMaterial(material)) {
        const dash = material.dashArray && material.dashArray[0] || null, gap = material.dashArray && material.dashArray.length > 1 && material.dashArray[1] || dash;
        const lineMatOptions = Utils.extend(lineBase, {
            linecap: material.lineCap ?? 'round',
            linejoin: material.lineJoin ?? 'round',
            linewidth: material.lineWidth ?? 1
        });
        return Utils.isNullOrEmpty(material.dashArray) ?
            new THREE.LineBasicMaterial(lineMatOptions)
            : new THREE.LineDashedMaterial(Utils.extend({ dashSize: dash, gapSize: gap }, lineMatOptions));
    }
    else if (isMaterial(material)) {
        const basic = mipMapMaterial(new THREE.MeshBasicMaterial(base));
        return basic;
    }
    else {
        return null;
    }
}
function materialToMaterials(frontMaterial, backMaterial, multiAllowed) {
    // material
    const multi = multiAllowed;
    // composite
    const materials = [];
    // front mat
    const material = materialToMaterial(frontMaterial);
    if (!Utils.isNull(material)) {
        material.side = THREE.FrontSide;
        materials.push(material);
    }
    // back mat
    if (!Utils.isNull(backMaterial)) {
        if (backMaterial == frontMaterial) {
            material.side = THREE.DoubleSide;
        }
        else {
            // multi-material
            const backsideMaterial = materialToMaterial(backMaterial);
            if (!Utils.isNull(backsideMaterial)) {
                backsideMaterial.side = THREE.BackSide;
                materials.push(backsideMaterial);
            }
        }
    }
    return multi && materials.length > 1 ? materials : material;
}
function load(item) {
    if (!Utils.isNullOrEmpty(item.content)) {
        const type = item.type?.toLowerCase();
        switch (type) {
            case 'obj':
                const obj = new THREE['OBJLoader']();
                return obj.parse(item.content);
            default:
                const loader = new THREE.ObjectLoader();
                return loader.parse(item.content);
        }
    }
    else {
        throw new Error('Missing content.');
    }
}
function meshType(geometry) {
    return geometry instanceof LineGeometry ? THREE.Line : THREE.Mesh;
}
function build(mesh) {
    if (Utils.isNullOrEmpty(mesh.geometry)) {
        return null;
    }
    const ctor = meshType(mesh.geometry);
    return new ctor();
}
function isTHREEAvailable() {
    return 'THREE' in window;
}
async function whenTHREEAvailable(...args) {
    const start = Date.now();
    var initialized = false;
    do {
        initialized = isTHREEAvailable();
        for (let pred of args) {
            initialized &&= pred();
        }
        if (!initialized) {
            await new Promise(resolve => setTimeout(resolve, 500));
        }
    } while (!initialized && (Date.now() - start) < 30000);
    if (!initialized) {
        throw new Error('The wait for THREE adapter initialization timed out.');
    }
}
let Pacem3DThreeAdapterElement = class Pacem3DThreeAdapterElement extends Pacem3DAdapterElement {
    constructor() {
        super(...arguments);
        this._renderer = null;
        this._camera = null;
        this._scene = null;
        this._orbitCtrls = null;
        this._objects = new WeakMap();
        this._dict = {};
        this._orbitControlsDelegate = (_) => {
        };
        this._ensureAndRefresh = (item, object3D, parent) => {
            object3D.matrixAutoUpdate = false;
            // choose whether to render the object or not
            if (object3D.visible = !item.hide) {
                // the modeling part
                // 1. pacem 3d mesh
                if (isMesh(item)) {
                    let mesh3D = object3D;
                    if (item.flags.indexOf(StalePropertyFlag.Geometry) >= 0) {
                        mesh3D.geometry.dispose();
                        // swap Line <-> Mesh according to the geometry
                        const mesh3D2 = build(item);
                        if (mesh3D2.constructor.name !== mesh3D.constructor.name) {
                            parent.remove(object3D);
                            delete this._dict[object3D.id];
                            object3D = mesh3D = mesh3D2;
                        }
                        // /swap
                        mesh3D.geometry = nodeGeometryToBufferGeometry(item.geometry);
                    }
                    if (item.flags.indexOf(StalePropertyFlag.Material) >= 0) {
                        if (Utils.isArray(mesh3D.material)) {
                            for (let m of mesh3D.material) {
                                m.dispose();
                            }
                        }
                        else {
                            mesh3D.material.dispose();
                        }
                        mesh3D.material = materialToMaterials(item.material) || consts.DEFAULT_MATERIAL();
                    }
                }
                // 2. 3d 'native' object
                else if (item instanceof Pacem3DObjectElement
                    && (item.flags.indexOf(StalePropertyFlag.Material) >= 0 || item.flags.indexOf(StalePropertyFlag.Geometry) >= 0)) {
                    // swap reference
                    object3D.parent && object3D.parent.remove(object3D);
                    object3D = load(item);
                }
                // 3. grouping object
                else if (item instanceof Pacem3DGroupElement) {
                    if (item.flags.indexOf(StalePropertyFlag.Children) >= 0) {
                        // remove all children
                        for (let j = object3D.children.length - 1; j >= 0; j--) {
                            const child = object3D.children[j];
                            object3D.remove(child);
                        }
                        // re-create
                        for (let child of item.items) {
                            this._addOrUpdateItem(child, object3D);
                        }
                    }
                }
                // 4. light
                else if (item instanceof Pacem3DLightElement) {
                    const light = object3D;
                    if (item.flags.indexOf(StalePropertyFlag.Light) >= 0) {
                        light.color = new THREE.Color(item.color);
                        light.intensity = item.intensity;
                    }
                }
                // the configuring part
                let posv = item.position; // || new THREE.Vector3(0, 0, 0);
                if (isUi3DObject(item)
                    && !Utils.isNull(item.transformMatrix)
                    && (item.flags.indexOf(StalePropertyFlag.Position) >= 0 || item.flags.indexOf(StalePropertyFlag.Transform) >= 0)) {
                    const positionMatrix = Geometry.LinearAlgebra.Matrix3D.from(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, posv.x, posv.y, posv.z, 1);
                    // item.transformMatrix is a local matrix thus
                    // prepend in the multiplication (i.e. FIRST apply transform THEN apply positioning)
                    const transformMatrix = Geometry.LinearAlgebra.Matrix3D.multiply(item.transformMatrix, positionMatrix);
                    THREE.Matrix4.prototype.set.apply(object3D.matrix, flattenMatrix3D(transformMatrix));
                    //object3D.updateMatrix();
                }
                else if (item.flags.indexOf(StalePropertyFlag.Position) >= 0) {
                    object3D.position.set(posv.x, posv.y, posv.z);
                    object3D.updateMatrix();
                }
            }
            object3D.userData = item.tag;
            if (Utils.isNull(parent.getObjectById(object3D.id))) {
                parent.add(object3D);
                this._dict[object3D.id] = item;
            }
            // clear flags
            item.flags.splice(0);
            return object3D;
        };
        //#endregion
    }
    _assertMonoStage(scene) {
        if (scene != this._3d && !Utils.isNull(this._3d)) {
            throw new Error('Stage mismatch.');
        }
    }
    dispose(scene) {
        // do nothing
    }
    snapshot(scene, background, type, quality) {
        if (scene != this._3d) {
            return Promise.resolve(null);
        }
        const jpeg = !Utils.isNullOrEmpty(background), mime = type ?? (jpeg ? 'image/jpeg' : null), compression = quality ?? (jpeg ? .9 : null);
        return new Promise((resolve, _) => {
            this.render();
            Utils.snapshotElement(this._stage, background, mime, compression).then(resolve);
        });
    }
    project(scene, point3D) {
        this._assertMonoStage(scene);
        // check feasibility
        if (Utils.isNull(this._camera) || Utils.isNull(this._stage))
            return null;
        //
        const stage = this._stage;
        const vector = new THREE.Vector3(point3D.x, point3D.y, point3D.z);
        const width = stage.width, height = stage.height;
        const widthHalf = width / 2, heightHalf = height / 2;
        const point = vector.project(this._camera);
        const x = (point.x * widthHalf) + widthHalf;
        const y = -(point.y * heightHalf) + heightHalf;
        return { x: x, y: y };
    }
    zoomFit(scene, ...items) {
        this._assertMonoStage(scene);
        return new Promise((resolve, reject) => {
            const camera = this._camera;
            const map = this._objects;
            const targets = items.filter(o => map.has(o)).map(o => map.get(o));
            if (Utils.isNull(camera) || Utils.isNullOrEmpty(targets)) {
                resolve();
            }
            fitCameraToSelection(camera, this._orbitCtrls, targets);
            resolve();
        });
    }
    getScene(scene) {
        this._assertMonoStage(scene);
        return this._scene;
    }
    raycast(scene, point, size, callback) {
        this._assertMonoStage(scene);
        if (isTHREEAvailable() && !Utils.isNull(this._camera)) {
            const camera = this._camera;
            const vector = new THREE.Vector2((point.x / size.width) * 2 - 1, -(point.y / size.height) * 2 + 1);
            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(vector, camera);
            const intersects = raycaster.intersectObjects(this._scene.children, true);
            let obj;
            let intersection;
            let j = 0;
            while (intersects.length > j && (intersection = intersects[j++]) && (obj = intersection.object)) {
                const item = this._dict[obj.id];
                if (item.inert) {
                    continue;
                }
                const v = intersection.point, x = v.x, y = v.y, z = v.z, point = { x, y, z };
                callback({ object: this._dict[obj.id], point });
                return;
            }
        }
        callback(null);
    }
    // #region ABSTRACT IMPLEMENTATION
    async initialize(container) {
        if (!Utils.isNull(this._stage)) {
            return this._stage;
        }
        this._3d = container;
        const wrapper = container.stage;
        const stage = this._stage = document.createElement('canvas');
        wrapper.innerHTML = '';
        wrapper.appendChild(stage);
        if (!isTHREEAvailable()) {
            await CustomElementUtils.importjs(consts.API_URI);
            await whenTHREEAvailable();
            await Promise.all([CustomElementUtils.importjs(consts.ORBIT_URI)]
                .concat(consts.LOADERS.map(js => CustomElementUtils.importjs(js))));
        }
        // here comes THREE.js
        this._scene = new THREE.Scene();
        // this._scene.add(new THREE.Mesh(new THREE.PlaneGeometry(1, 1, 1, 1), consts.DEFAULT_MATERIAL()));
        this.invalidateSize(container);
        let w = this._stage.width, h = this._stage.height;
        //if (!ctrl.renderer)
        let parameters = {
            canvas: stage, antialias: true, stencil: false, alpha: container.transparent ?? true //, clearAlpha: 1
        };
        this._renderer = new THREE.WebGLRenderer(parameters);
        this._renderer.setSize(w, h);
        if (container.background && !container.transparent) {
            let clr = container.background;
            if (typeof clr === 'string') {
                clr = Colors.parse(clr);
            }
            const theejsClr = new THREE.Color(clr.r, clr.g, clr.b);
            this._renderer.setClearColor(theejsClr, clr.a);
        }
        this.orbit = container.orbit;
        return stage;
    }
    _setCamera(cam) {
        this._camera = cam;
        this.invalidateSize(this._3d);
        this._disposeOrbitControls();
        // re-init
        if (this.orbit) {
            this._initOrbitControls();
        }
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (name === 'orbit') {
            if (val === true)
                this._initOrbitControls();
            else
                this._disposeOrbitControls();
        }
    }
    _initOrbitControls() {
        const cam = this._camera;
        if (Utils.isNull(this._3d) || Utils.isNull(cam)) {
            return;
        }
        const controls = this._orbitCtrls = new THREE['OrbitControls'](cam, this._3d.stage);
        controls.addEventListener('change', this._orbitControlsDelegate);
    }
    _disposeOrbitControls() {
        const controls = this._orbitCtrls;
        if (Utils.isNull(controls)) {
            return;
        }
        controls.removeEventListener('change', this._orbitControlsDelegate);
        controls.dispose();
    }
    _updateRenderer(container, renderer = this._renderer) {
        if (!container.transparent) {
            if (container.background) {
                let clr = container.background;
                if (typeof clr === 'string') {
                    clr = Colors.parse(clr);
                }
                const theejsClr = new THREE.Color(clr.r, clr.g, clr.b);
                renderer.setClearColor(theejsClr, clr.a);
            }
            else {
                renderer.setClearColor(null);
            }
        }
        else {
            renderer.setClearAlpha(0);
        }
    }
    invalidateSize(scene, size = scene.size) {
        this._assertMonoStage(scene);
        if (!isTHREEAvailable() || Utils.isNull(size)) {
            return;
        }
        let w = size.width;
        let h = size.height;
        this._stage.width = w;
        this._stage.height = h;
        let camera = this._camera;
        if (camera instanceof THREE.PerspectiveCamera) {
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
        }
        if (this._renderer) {
            this._renderer.setSize(w, h);
            this._updateRenderer(scene);
        }
    }
    removeItem(scene, item) {
        this._assertMonoStage(scene);
        const bag = this._objects;
        const obj3d = bag.get(item);
        if (!Utils.isNull(obj3d)) {
            this._scene.remove(obj3d);
            delete this._dict[obj3d.id];
            bag.delete(item);
        }
    }
    updateItem(scene, item) {
        this._assertMonoStage(scene);
        const bag = this._objects;
        if (!Utils.isNull(item.parent) && bag.has(item.parent)) {
            const parent3d = bag.get(item.parent);
            this._addOrUpdateItem(item, parent3d);
        }
        else {
            this._addOrUpdateItem(item);
        }
    }
    addItem(scene, item) {
        this._assertMonoStage(scene);
        this._addOrUpdateItem(item);
    }
    _addOrUpdateItem(item, parent = this._scene) {
        if (!isTHREEAvailable() || Utils.isNull(parent)) {
            return;
        }
        if (item instanceof Pacem3DCameraElement) {
            if (item instanceof Pacem3DPerspectiveCameraElement) {
                var pcam;
                if (this._camera instanceof THREE.PerspectiveCamera) {
                    var pcam = this._camera;
                }
                else {
                    pcam = new THREE.PerspectiveCamera();
                }
                const lookAtVector = new THREE.Vector3(item.lookAt.x, item.lookAt.y, item.lookAt.z);
                pcam.lookAt(lookAtVector);
                pcam.position.set(item.position.x, item.position.y, item.position.z);
                pcam.fov = item.fov;
                pcam.aspect = item.aspect;
                pcam.near = item.near;
                pcam.far = item.far;
                //
                this._setCamera(pcam);
                //
                const orbit = this._orbitCtrls;
                if (!Utils.isNull(orbit) && orbit.object === pcam) {
                    orbit.target = lookAtVector;
                    orbit.update();
                }
            }
            else if (item instanceof Pacem3DOrthographicCameraElement) {
                // bunch of duplicated code
                // no need to refactor since it's a deprecating branch of development
                var ocam;
                if (this._camera instanceof THREE.OrthographicCamera) {
                    var ocam = this._camera;
                }
                else {
                    ocam = new THREE.OrthographicCamera();
                }
                const lookAtVector = new THREE.Vector3(item.lookAt.x, item.lookAt.y, item.lookAt.z);
                ocam.lookAt(lookAtVector);
                ocam.position.set(item.position.x, item.position.y, item.position.z);
                ocam.left = item.left;
                ocam.right = item.right;
                ocam.top = item.top;
                ocam.bottom = item.bottom;
                ocam.near = item.near;
                ocam.far = item.far;
                //
                this._setCamera(ocam);
                //
                const orbit = this._orbitCtrls;
                if (!Utils.isNull(orbit) && orbit.object === ocam) {
                    orbit.target = lookAtVector;
                    orbit.update();
                }
            }
            else {
                // cam not supported
                throw new Error(`Camera ${item} not supported.`);
            }
        }
        else {
            if (!this._objects.has(item)) {
                // adding
                const then = (object3D) => {
                    // clear flags
                    // item.flags.splice(0);
                    if (object3D) {
                        object3D = this._ensureAndRefresh(item, object3D, parent);
                        this._objects.set(item, object3D);
                    }
                    else {
                        // object not built
                        // throw?
                    }
                };
                // TODO: switch to interface matching
                if (item instanceof Pacem3DObjectElement) {
                    const mesh = load(item);
                    then(mesh);
                }
                else if (item instanceof Pacem3DMeshElement) {
                    // beware! a change in the geometry could cause a change in the object3D type
                    const mesh = build(item);
                    then(mesh);
                }
                else if (item instanceof Pacem3DLightElement) {
                    const light = lightToLight(item);
                    then(light);
                }
                else if (item instanceof Pacem3DGroupElement) {
                    const group = new THREE.Group();
                    then(group);
                }
            }
            else {
                // updating
                const obj = this._objects.get(item);
                const objOut = this._ensureAndRefresh(item, obj, parent);
                if (objOut != obj) {
                    this._objects.set(item, objOut);
                }
            }
        }
    }
    render() {
        if (this._camera) {
            this._renderer.render(this._scene, this._camera);
        }
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Boolean })
], Pacem3DThreeAdapterElement.prototype, "orbit", void 0);
Pacem3DThreeAdapterElement = __decorate([
    CustomElement({ tagName: P + '-3d-three-adapter' })
], Pacem3DThreeAdapterElement);
export { Pacem3DThreeAdapterElement };
